import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
    LoginResponse, LoginRequest, ForgotPassword,
    ChangePasswordParams, ChangePasswordResponse, SignUpRequest,
    ConfirmEmailRequest, PasswordPolicyResponse, OtpRequest, ChangePasswordParamsStruct,
    BiometricRequest, UccOtpLoginResponse, UccOtpLoginRequest, WhatsNewData, proceedLoginParams, PinAuthResponse
} from '../../../app.interfaces';
import { Utils } from '../../../_helpers/utilities';
import { MPinParams, OtpGenerateRequest, OtpVerifyRequest, PanVerifyRequestResponse, genericResponse, tokenExpiration,PersistanceTokenResponse, restLoginRequest, restLoginResponse, restAccessTokenRequest, restUserDetails, restTotpResponse, QuickLinksRequest, QuickLinksResponse } from 'sharedweb/app.interface';
import { applicationUrls } from 'sharedweb/helpers/urls';
import { tap } from 'rxjs/operators';
import { AccessType, PlatformType } from 'sharedweb/app.structs';
import { PlatformType as PF } from 'src/app/app.structs';
import { CACHE_KEYS, CURRENT_PLATFORMTYPE, EVENTS } from 'src/app/_helpers';
import { BehaviorSubject, of } from 'rxjs';
import * as moment from 'moment';
import { TWO_FA_EOD_TOKEN_REFRESH_TIME } from 'sharedweb/helpers/shared-constants';
import { ActivatedRoute } from '@angular/router';
import { WhatsNewComponent } from '../../components/whats-new/whats-new.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ConfigurationService } from '../configuration/configuration.service';
import { EventsService } from '../events/events.service';
import { AccountsService } from 'src/app/dashboard/_modules/accounts/providers/accounts-service/accounts.service';

export class JWTUtilClass {

    username: string;
    email: string;
    first_name: string;
    last_name: string; 
    user_type: string;
    token_type: string;
    device_id: string; 
    session_key: string;
    delta: number;
    created_at: number;
    exp: number;

    constructor(
        token: string,
        isServerFetch?: boolean
    ) {
        const claims = JSON.parse(atob(token.split('.')[1]));
        Object.keys(claims).forEach(key => {
            this[key] = claims[key];
        });
        /**
         * getting the difference between current system time and 
         * token creation time and storing in delta
         */
        if (isServerFetch) {
            Utils.storageHelper.deltaValue = moment().unix() - this.created_at;
        }

        this.delta = Utils.storageHelper.deltaValue;
    }
    
    get isExpired() {
        if (!this.exp) { return true }
        // Token is refreshed 25 seconds prior to actual expiry time.
        // We will not wait for exact expiry.
        // Done for handling cases in which there might be some time difference
        // different modules and case in which a request is sent just before
        // actual expiry time.
        return ((this.exp + this.delta) - moment().unix()) <= TWO_FA_EOD_TOKEN_REFRESH_TIME;
    }
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    restResponse: restUserDetails;
    passWordPolicyResponse: PasswordPolicyResponse;
    accessTokenObj: JWTUtilClass;
    consent_id: string = '';

    constructor(
        private _httpClient: HttpClient,
        private _activatedRoute: ActivatedRoute,
        private dialog: MatDialog,
        private configService: ConfigurationService,
        private eventService: EventsService,
        private accountService: AccountsService
    ) {
        if(Utils.storageHelper.userInfo && Utils.storageHelper.userInfo.access_token){
            this.accessTokenObj = new JWTUtilClass(Utils.storageHelper.userInfo.access_token);
        }
     }

    login(loginRequest: LoginRequest, headers?: any) {
        const url = applicationUrls.loginUrl;
        return this._httpClient.post<LoginResponse>(url, loginRequest, { headers });
    }
    
    resend(otpRequest: Partial<OtpRequest>) {
        const url = applicationUrls.verifyOtp;
        return this._httpClient.put(url, otpRequest);
    }

    sendOtp(otpRequest: OtpRequest) {
        const url = applicationUrls.verifyOtp;
        return this._httpClient.post<any>(url, otpRequest);
    }

    getTwoFAToken(persistanceToken:string){
        const url = `${applicationUrls.twoFAToken}${persistanceToken}/`;
        return this._httpClient.get<PersistanceTokenResponse>(url)
    }

    forgot(forgotRequest: ForgotPassword) {
        const url = applicationUrls.recoverPasswordInitUrl;
        return this._httpClient.post<ChangePasswordResponse>(url, forgotRequest);
    }

    uccOtpLogin(uccOtpRequest: UccOtpLoginRequest, headers?: any) {
        const url = applicationUrls.uccOtpLogin;
        return this._httpClient.post<UccOtpLoginResponse>(url, uccOtpRequest, { headers });
    }

    logout(forcedLogout?: boolean, forcedLogoutAll?: boolean, sessionKey?: string, platform?: string, refresh?: boolean) {
        const url = applicationUrls.logoutUrl;
        const token = `${Utils.storageHelper.userInfo.tokenType} ${Utils.storageHelper.userInfo.access_token}`;
        // Clear Token in Storage
        //RT-11331 Checking if platform desktop,then not setting session key and platform type as web. 
        if (!forcedLogoutAll && !sessionKey && (platform != AccessType.desktop.toLowerCase())) {
            sessionKey = Utils.storageHelper.userInfo.sessionKey;
            if(platform!=PF.UALGO_MOBILE){
                platform = CURRENT_PLATFORMTYPE
            }
        }

        const logoutPayload = {};
        if (forcedLogout) {
            logoutPayload['is_force'] = 1;
        }

        if (forcedLogoutAll) {
            logoutPayload['is_force_logout'] = 1;
            platform = PlatformType.All;
        }
        if (sessionKey) {
            logoutPayload['session_key'] = sessionKey;
        }
        logoutPayload['platform'] = platform;
        console.log(logoutPayload);

        return this._httpClient.get(url, {
            headers: {
                Authorization: token
            }, params: <any>logoutPayload
        }).pipe(tap((res) => {
            if ((platform != AccessType.desktop.toLowerCase()) && (!sessionKey || (sessionKey === Utils.storageHelper.userInfo.sessionKey))) {
                if (res) {
                    Utils.storageHelper.clear();
                    if(!refresh) {
                        if (this._activatedRoute.snapshot.queryParams['redirect_uri']) {
                            this.authorizeUser(this._activatedRoute.snapshot.queryParams);
                        } else {
                            window.location.href = '/login';
                        }    
                    }
                }
            }
        }));
    }

    authorizeUser(params) {
        this.accountService.getGrantDetails(params).subscribe((r) => {
            this.accountService.authoriseGrantDetails(r).subscribe(async (r) => {
                location.replace(r.uri);
            });
        });
    }

    changePassword(changePasswordParams: ChangePasswordParams, token: string) {
        const url = applicationUrls.changePasswordUrl;
        // RE-2454 As required from Server end, no need to send token in header for change password.
            return this._httpClient.post<ChangePasswordResponse>(url, changePasswordParams);
    }

    setBiometrics(biometricRequest: BiometricRequest){
        const url = applicationUrls.setBiometrics;
        return this._httpClient.post(url,biometricRequest);
    }

    changePasswordPage(changePasswordParams: ChangePasswordParamsStruct, token: string) {
        const url = applicationUrls.changePasswordInitializeUrl;
        const body = {...changePasswordParams, token}
        if (token) {
            return this._httpClient.post<ChangePasswordResponse>(url, body);
        }
    }

    signUp(credentials: SignUpRequest, headers?: any) {
        const url = applicationUrls.signup;
        return this._httpClient.post(url, credentials, { headers });
    }

    confirmEmail(key: string) {
        const url = applicationUrls.emailConfirmationUrl;
        const request: ConfirmEmailRequest = {
            token: key
        };
        return this._httpClient.get(url, {
            params: <any>request
        });
    }

    getPasswordPolicy() {
        if(this.passWordPolicyResponse){
            return of(this.passWordPolicyResponse);
        }
        const url = applicationUrls.getPasswordPolicy;
        return this._httpClient.get<PasswordPolicyResponse>(url).pipe(tap((res) => {
            this.passWordPolicyResponse = res;
         }));
    }

    setMPin(request: MPinParams) {
        const url = applicationUrls.setMPin;
        return this._httpClient.post<any>(url, request);
    }

    check2FA(request: MPinParams) {
        const url = applicationUrls.verify2FA;
        return this._httpClient.post<any>(url, request);
    }

    check2FABiometric(request: MPinParams) {
        const url = applicationUrls.verifyBiometric2FA;
        return this._httpClient.post<any>(url, request);
    }

    fetchLoginToken(request: proceedLoginParams) {
        const url = applicationUrls.proceed2FA;
        return this._httpClient.post<PinAuthResponse>(url, request);
    }

    forgotMPin(request: MPinParams) {
        const url = applicationUrls.forgotMPin;
        return this._httpClient.post<any>(url, request);
    }
    get isAuthenticated() {
        return !!Utils.storageHelper.userInfo;
    }

    checkTokenExpiration(token: string) {
        const url = applicationUrls.checkTokenExpirationUrl;
        return this._httpClient.post<tokenExpiration>(url, {token});
    }

    generateOtp(request:OtpGenerateRequest){
        const url = applicationUrls.generateOtp;
        return this._httpClient.post<genericResponse>(url,request)
    }

    verifyOtp(request: OtpVerifyRequest, headers?: any){
        const url = applicationUrls.verifyOtpTotp;
        return this._httpClient.post<genericResponse>(url,request, { headers })
    }

    verifyPan(forgotRequest: ForgotPassword, headers?: any){
        const url = applicationUrls.verifyPAN;
        return this._httpClient.post<PanVerifyRequestResponse>(url, forgotRequest, { headers });
    }

    //Rest

    restLogin(request: restLoginRequest) {
        const url = applicationUrls.getRestLoginUrl;
        return this._httpClient.post<restLoginResponse>(url, request);
    }

    getRestUser(appKey:string) {
        if(this.restResponse){
            return of(this.restResponse);
        }
        const url = applicationUrls.getRestUserDetails + appKey;
        return this._httpClient.get<restUserDetails>(url).pipe(tap((res) => {
           this.restResponse = res;
        }));
    }

    verifyRestTOTP(request: restAccessTokenRequest) {
        const url = applicationUrls.getVerifyRestTotpOtp;
        return this._httpClient.post<restTotpResponse>(url, request);
    }

    displayWhatsNew() {
        let whatsNewData = this.configService.remoteConfigCacheObj.WHATS_NEW;
        if(!whatsNewData || (whatsNewData && !whatsNewData?.SHOW_WHATS_NEW)){
            return;
        }
        const get_url = applicationUrls.getCacheUrl;
        this._httpClient.get<any>(get_url , {
            params: {
                key: CACHE_KEYS.whatsNew
            }
        }).subscribe((response) => {
            let id = this.configService.remoteConfigCacheObj.WHATS_NEW['id'];
            let whatsNewId = Utils.storageHelper.whatsNewId;
            const set_url = applicationUrls.setCacheUrl;
            if(response.data.length === 0 || response.data != id){
                Utils.storageHelper.whatsNewId = id;
                this._httpClient.post(set_url, {
                    key : CACHE_KEYS.whatsNew,
                    data : JSON.stringify(id)
                }).subscribe();
                this.openWhatsNew();
                this.setWhatsNewData();
            } else if(response.data == id){
                if(whatsNewId != id){
                    Utils.storageHelper.whatsNewId = id;
                    this.eventService.publish(EVENTS.WHATS_NEW_FLAG, true);
                }
                return;
            }
        })
    }

    setWhatsNewData(){
        let whatsNewTabs = [];
        let data = this.configService.remoteConfigCacheObj.WHATS_NEW['data'];
        data.forEach((element: WhatsNewData) => {
            if(element.tab){
                whatsNewTabs.push(element.tab);
            }
        });
        this.eventService.publish(EVENTS.WHATS_NEW_TABS, whatsNewTabs)
        this.eventService.publish(EVENTS.WHATS_NEW_FLAG, true);
    }

    openWhatsNew() {
        this.dialog.open(WhatsNewComponent, {
            width : "800px",
            height : "500px",
            panelClass: "whats-new-dialog",
        })
    }


    getQuickLinks(request: QuickLinksRequest) {
        const url = applicationUrls.getQuickLinks;
        return this._httpClient.post<QuickLinksResponse>(url, request);
    }
}
